import PropTypes from 'prop-types'
import React from 'react'
import { findDOMNode } from 'react-dom'
import { FormattedMessage } from 'react-intl'
import Scrollbars from 'components/Scrollbars'
import KanBoIcon from 'components/KanBoIcon'
import {
  LeftSidebarView,
  ViewBody
} from 'components/Sidebar'
import Activities from 'components/Activities'
import Notification from '../Notification'
import NotificationSettings from '../UserSettings/components/Notifications'
import withLeftSidebar from 'components/Sidebar/withLeftSidebar'

const getRelativeOffsetTop = (node, relativeParent) => {
  let offsetTop = 0
  let currentNode = node

  while (currentNode && currentNode !== relativeParent) {
    offsetTop += currentNode.offsetTop

    currentNode = currentNode.offsetParent
  }

  return offsetTop
}

export class Notifications extends React.Component {
  static propTypes = {
    leftSidebar: PropTypes.object.isRequired,

    isLoaded: PropTypes.bool.isRequired,
    isLoading: PropTypes.bool.isRequired,
    lastLoadedId: PropTypes.number,
    currentUser: PropTypes.object.isRequired,
    activities: PropTypes.array.isRequired,
    unreadOnly: PropTypes.bool.isRequired,
    unreadNotificationCount: PropTypes.number.isRequired,

    fetchActivities: PropTypes.func.isRequired,
    requestMarkAllNotificationsAsRead: PropTypes.func.isRequired,
    setNotificationsUnreadOnly: PropTypes.func.isRequired,
  }

  state = {
    prevNotification: null,
    nextNotification: null,
    loadNextNotification: false
  }

  componentDidMount () {
    if (this.props.unreadOnly) {
      this.props.setNotificationsUnreadOnly(false)
    }

    this.updateTopNotification()
  }

  componentDidUpdate (prevProps, prevState) {
    if (
      this.props.activities !== prevProps.activities
    ) {
      this.updateTopNotification()
    }

    if (
      (
        this.state.loadNextNotification === true &&
        prevState.loadNextNotification === false
      ) || (
        this.state.loadNextNotification &&
        this.props.activities !== prevProps.activities
      )
    ) {
      if (this.activitiesRef) this.activitiesRef.loadActivities()
    }

    if (
      this.state.loadNextNotification === false &&
      prevState.loadNextNotification === true &&
      this.state.nextNotification &&
      this.state.nextNotification !== true
    ) {
      this.handleShowNextNotificationClick()
    }
  }

  setContainerRef = (ref) => {
    this.containerRef = ref
  }

  setActivitiesRef = (ref) => {
    this.activitiesRef = ref
  }

  setActivityRef = (ref, id) => {
    if (ref) {
      this['activityRef-' + id] = ref
    } else if (this['activityRef-' + id]) {
      delete this['activityRef-' + id]
    }
  }

  getActivityRef = (id) => {
    return this['activityRef-' + id]
  }

  handleBackClick = () => {
    this.props.leftSidebar.pop()
  }

  handleMarkAllAsReadClick = () => {
    this.props.requestMarkAllNotificationsAsRead()
  }

  handleSettingsClick = () => {
    this.props.leftSidebar.push(NotificationSettings)
  }

  handleShowPreviousNotificationClick = () => {
    const { prevNotification } = this.state

    if (prevNotification) {
      const activityRef = this.getActivityRef(prevNotification.id)
      if (activityRef) {
        const activityDOMNode = findDOMNode(activityRef)
        if (activityDOMNode) {
          const containerDOMNode = findDOMNode(this.containerRef)

          if (containerDOMNode) {
            containerDOMNode.scrollTop = getRelativeOffsetTop(activityDOMNode, containerDOMNode)
          }
        }
      }
    }
  }

  handleShowNextNotificationClick = () => {
    const { nextNotification } = this.state

    if (nextNotification) {
      if (nextNotification === true) {
        this.setState({
          loadNextNotification: true
        })
      } else {
        const activityRef = this.getActivityRef(nextNotification.id)
        if (activityRef) {
          const activityDOMNode = findDOMNode(activityRef)
          if (activityDOMNode) {
            const containerDOMNode = findDOMNode(this.containerRef)

            if (containerDOMNode) {
              containerDOMNode.scrollTop = getRelativeOffsetTop(activityDOMNode, containerDOMNode)
            }
          }
        }
      }
    }
  }

  handleScroll = (event) => {
    this.updateTopNotification()
  }

  updateTopNotification = () => {
    const {
      activities,
      unreadNotificationCount
    } = this.props
    const {
      loadNextNotification
    } = this.state

    if (!this.containerRef) return

    const containerDOMNode = findDOMNode(this.containerRef)

    if (!containerDOMNode) return

    const containerScrollTop = containerDOMNode.scrollTop
    const scrolledToBottom = containerScrollTop === containerDOMNode.scrollHeight - containerDOMNode.offsetHeight

    let prevNotification = null
    let nextNotification = null

    const unreadActivities = activities.filter(activity => !activity.wasRead)

    const length = unreadActivities.length
    for (let i = 0; i < length; i++) {
      const activity = unreadActivities[i]
      if (!activity.wasRead) {
        const activityRef = this.getActivityRef(activity.id)
        if (activityRef) {
          const activityDOMNode = findDOMNode(activityRef)
          if (activityDOMNode) {
            const activityTop = getRelativeOffsetTop(activityDOMNode, containerDOMNode)
            if (activityTop - 5 < containerScrollTop && containerScrollTop < activityTop + 5) {
              continue
            }

            if (activityTop - 5 < containerScrollTop) {
              prevNotification = activity
            } else if (!scrolledToBottom && activityTop + 5 > containerScrollTop) {
              nextNotification = activity
              break
            }
          }
        }
      }
    }

    if (!nextNotification && !scrolledToBottom && length < unreadNotificationCount) {
      nextNotification = true
    }

    if (loadNextNotification && nextNotification && nextNotification !== true) {
      this.setState({
        prevNotification,
        nextNotification,
        loadNextNotification: false
      })
    } else {
      this.setState({
        prevNotification,
        nextNotification
      })
    }
  }

  renderActivity = ({ activityRef, ...props }) => {
    return (
      <Notification
        {...props}
        activityRef={(ref, id) => {
          this.setActivityRef(ref, id)

          if (activityRef) activityRef(ref, id)
        }}
      />
    )
  }

  render () {
    const {
      isLoaded,
      isLoading,
      lastLoadedId,
      currentUser,
      activities,
      unreadOnly,
      unreadNotificationCount,

      fetchActivities
    } = this.props

    const {
      prevNotification,
      nextNotification,
      loadNextNotification
    } = this.state

    return (
      <LeftSidebarView
        title={<FormattedMessage id='NOTIFICATIONS_ALL_HEADER' />}
        buttons={[
          [
            {
              icon: <KanBoIcon name='kanbo-16-previous' />,
              title: <FormattedMessage id='NOTIFICATIONS_PREVIOUS' />,
              disabled: !prevNotification,
              onClick: this.handleShowPreviousNotificationClick
            }, {
              icon: <KanBoIcon name='kanbo-16-next' />,
              title: <FormattedMessage id='NOTIFICATIONS_NEXT' />,
              disabled: !nextNotification || loadNextNotification,
              onClick: this.handleShowNextNotificationClick
            }
          ]
        ]}
        menu={[{
          icon: <KanBoIcon name='kanbo-16-tasks' />,
          title: <FormattedMessage id='NOTIFICATIONS_MARK_ALL_AS_READ' />,
          onClick: this.handleMarkAllAsReadClick,
          disabled: !unreadNotificationCount
        }, {
          icon: <KanBoIcon name='kanbo-16-settings' />,
          title: <FormattedMessage id='SETTINGS' />,
          onClick: this.handleSettingsClick
        }]}
      >
        <Scrollbars
          component={ViewBody}
          ref={this.setContainerRef}
          onScroll={this.handleScroll}
        >
          {unreadOnly === false &&
            <Activities
              key={unreadOnly}
              ref={this.setActivitiesRef}
              context='notifications'
              isLoaded={isLoaded}
              isLoading={isLoading}
              lastLoadedId={lastLoadedId}
              currentUser={currentUser}
              activities={activities}
              activityComponent={this.renderActivity}
              fetchActivities={fetchActivities}
            />
          }
        </Scrollbars>
      </LeftSidebarView>
    )
  }
}

export default withLeftSidebar(Notifications)
